import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userAdminApi } from '@/api/recognition';

interface Props {
  onNext: () => void;
}

interface ColumnInfo {
  name: string;
  required: boolean;
  descKey: string;
}

// La región (Europa/USA) ya no es una columna: se elige en un combo antes de subir el archivo
// (ver StepUploadFile), así se evita el error de tipeo/idioma que tenía escribirla en el CSV.
//
// LOCAL ID es la ÚNICA columna que de verdad bloquea una fila si viene vacía — el resto es
// tolerado por el backend (EuropeUsaPadronParser/ImportEuropeUsaCommandHandler: valores ausentes
// se dejan en null o se completan con lo que la persona ya tenía cargado). Antes esta tabla
// marcaba 17 columnas como "Obligatoria" sin que el backend las exigiera.
const COLUMNS: ColumnInfo[] = [
  { name: 'EMPLOYEE DELETED', required: false, descKey: 'admin.bulkUsers.col.employeeDeleted' },
  { name: 'LOCAL ID', required: true, descKey: 'admin.bulkUsers.col.localId' },
  { name: 'EMPLOYEE FIRST NAME', required: false, descKey: 'admin.bulkUsers.col.firstName' },
  { name: 'EMPLOYEE LAST NAME', required: false, descKey: 'admin.bulkUsers.col.lastName' },
  { name: 'EMPLOYEE 2ND LAST NAME', required: false, descKey: 'admin.bulkUsers.col.secondLastName' },
  { name: 'EMPLOYEE COMPLETE NAME', required: false, descKey: 'admin.bulkUsers.col.completeName' },
  { name: 'EMAIL', required: false, descKey: 'admin.bulkUsers.col.email' },
  { name: 'BU', required: false, descKey: 'admin.bulkUsers.col.bu' },
  { name: 'SITE ID', required: false, descKey: 'admin.bulkUsers.col.siteId' },
  { name: 'SITE', required: false, descKey: 'admin.bulkUsers.col.site' },
  { name: 'ORGANIZATIONAL DIRECTION ID', required: false, descKey: 'admin.bulkUsers.col.orgDirectionId' },
  { name: 'ORGANIZATIONAL DIRECTION', required: false, descKey: 'admin.bulkUsers.col.orgDirection' },
  { name: 'FUNCTIONAL DIRECTION ID', required: false, descKey: 'admin.bulkUsers.col.funcDirectionId' },
  { name: 'FUNCTIONAL DIRECTION', required: false, descKey: 'admin.bulkUsers.col.funcDirection' },
  { name: 'DEPARTMENT ID', required: false, descKey: 'admin.bulkUsers.col.departmentId' },
  { name: 'DEPARTMENT', required: false, descKey: 'admin.bulkUsers.col.department' },
  { name: 'JOB POSITION', required: false, descKey: 'admin.bulkUsers.col.jobPosition' },
  { name: 'MANAGER', required: false, descKey: 'admin.bulkUsers.col.manager' },
  { name: 'MANAGER ID', required: false, descKey: 'admin.bulkUsers.col.managerId' },
  { name: 'EMAIL_MANAGER', required: false, descKey: 'admin.bulkUsers.col.managerEmail' },
  { name: 'HIRE DATE', required: false, descKey: 'admin.bulkUsers.col.hireDate' },
  { name: 'DATE BIRTH', required: false, descKey: 'admin.bulkUsers.col.birthDate' },
  { name: 'NOTIFICATION', required: false, descKey: 'admin.bulkUsers.col.notification' },
  { name: 'COUNTRY', required: false, descKey: 'admin.bulkUsers.col.country' },
];

/** Paso 1 del wizard de Usuarios: qué es cada columna y descarga de la plantilla real del backend. */
export function StepInstructions({ onNext }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const [templateError, setTemplateError] = useState(false);

  const downloadTemplate = async () => {
    setTemplateError(false);
    try {
      const csv = await userAdminApi.europeUsaImportTemplate();
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'padron-europa-eua.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      if (import.meta.env.DEV) console.error('No se pudo descargar la plantilla del padrón:', error);
      setTemplateError(true);
    }
  };

  return (
    <div className="bulk-step bulk-step--instructions">
      <p className="bulk-intro">{t('admin.bulkUsers.importHint')}</p>

      <table className="bulk-columns-table">
        <tbody>
          {COLUMNS.map((column) => (
            <tr key={column.name}>
              <td className="bulk-col-name">{column.name}</td>
              <td>
                <span className={`bulk-col-tag ${column.required ? 'bulk-col-tag--req' : ''}`}>
                  {column.required ? t('bulk.colRequired') : t('bulk.colOptional')}
                </span>
              </td>
              <td className="bulk-col-desc">{t(column.descKey)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="bulk-actions">
        <button type="button" className="btn btn--secondary" onClick={() => void downloadTemplate()}>
          <i className="fas fa-download" /> {t('bulk.downloadTemplate')}
        </button>
        {templateError && (
          <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {t('bulk.templateError')}</p>
        )}
      </div>

      <div className="bulk-wizard-nav">
        <span />
        <button type="button" className="btn btn--send" onClick={onNext}>
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepInstructions;
